import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, TrendingUp, Activity, Search, X, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { track } from "../lib/api";

/**
 * Live activity nudges — periodic toast notifications that arrive in the
 * bottom-right (left in RTL) corner. Designed to give the dashboard a
 * "the AI is alive and scanning" feeling during demos.
 *
 * - First toast appears after ~25s (lets the user explore the page first)
 * - Then every 60-90s a new one arrives
 * - Toast auto-dismisses after 10s
 * - User can mute for the session via the X button
 * - Won't appear on /admin, /chat or while the Morning Briefing is open
 *
 * NOTE: The content is plausible demo-grade copy derived from the headline
 * data. It is not making up business facts — counts, percentages, and item
 * names come from the same insights pipeline that powers the dashboard.
 */
const SESSION_MUTE_KEY = "optygo_live_nudges_muted";

export default function LiveNudges({ headline, recommendations, hidden = false }) {
  const [current, setCurrent] = useState(null);
  const [muted, setMuted] = useState(() => sessionStorage.getItem(SESSION_MUTE_KEY) === "1");
  const navigate = useNavigate();

  useEffect(() => {
    if (hidden) return;
    if (muted) return;
    if (!headline) return;
    if (typeof window === "undefined") return;

    const path = window.location.pathname;
    if (path.startsWith("/admin") || path.startsWith("/chat") || path.startsWith("/login")) return;

    const nudges = buildNudges(headline, recommendations);
    if (nudges.length === 0) return;

    let idx = 0;
    let dismissTimer;
    let nextTimer;

    const showNext = () => {
      const n = nudges[idx % nudges.length];
      setCurrent({ ...n, _id: Date.now() });
      track("nudge_shown", path, { kind: n.kind });
      idx += 1;

      // Auto-dismiss after 10s
      dismissTimer = setTimeout(() => {
        setCurrent(null);
        // Schedule the next one
        nextTimer = setTimeout(showNext, 60_000 + Math.random() * 30_000);
      }, 10_000);
    };

    // First nudge fires after ~25 seconds
    const firstTimer = setTimeout(showNext, 25_000);

    return () => {
      clearTimeout(firstTimer);
      clearTimeout(dismissTimer);
      clearTimeout(nextTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headline, recommendations, muted]);

  if (hidden) return null;
  if (muted) return null;

  const handleAction = () => {
    if (!current) return;
    track("nudge_action", window.location.pathname, { kind: current.kind });
    const target = current.action;
    setCurrent(null);
    navigate(target);
  };

  const handleDismiss = (silenceForSession = false) => {
    track("nudge_dismiss", window.location.pathname, {
      kind: current?.kind,
      silenced: silenceForSession,
    });
    if (silenceForSession) {
      sessionStorage.setItem(SESSION_MUTE_KEY, "1");
      setMuted(true);
    }
    setCurrent(null);
  };

  return (
    <AnimatePresence>
      {current && (
        <motion.div
          key={current._id}
          initial={{ opacity: 0, y: 12, x: 12 }}
          animate={{ opacity: 1, y: 0, x: 0 }}
          exit={{ opacity: 0, y: 12, x: 12 }}
          transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
          // bottom-left because RTL puts the FAB on the bottom-right
          className="fixed bottom-20 left-4 lg:bottom-6 lg:left-6 z-40 w-[290px] max-w-[calc(100vw-2rem)]"
          data-testid="live-nudge"
        >
          <div className="relative bg-card border border-border rounded-md shadow-2xl overflow-hidden">
            {/* Live ping */}
            <div className="absolute top-3 right-3 flex items-center gap-1.5">
              <span className="relative inline-flex">
                <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                <span className="absolute inset-0 w-1.5 h-1.5 rounded-full bg-primary animate-ping opacity-75" />
              </span>
              <span className="text-[9px] uppercase tracking-widest font-mono text-primary">live</span>
            </div>

            {/* Close button */}
            <button
              onClick={() => handleDismiss(false)}
              className="absolute top-2.5 left-2.5 p-1 text-zinc-600 hover:text-foreground transition-colors"
              data-testid="nudge-close"
              aria-label="סגור"
            >
              <X className="w-3 h-3" />
            </button>

            {/* Content */}
            <div className="p-4 pt-7">
              <div className="flex items-start gap-2.5">
                <div className={`w-7 h-7 rounded-md flex items-center justify-center shrink-0 ${current.iconBg}`}>
                  <current.icon className={`w-3.5 h-3.5 ${current.iconColor}`} strokeWidth={1.75} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-0.5">
                    Optygo Pulse · {current.eyebrow}
                  </div>
                  <div className="text-sm font-medium text-foreground leading-snug">
                    {current.title}
                  </div>
                  <div className="text-[11px] text-zinc-400 mt-1 leading-relaxed">
                    {current.body}
                  </div>
                </div>
              </div>

              <button
                onClick={handleAction}
                className="mt-3 w-full flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md bg-primary/10 hover:bg-primary/20 text-primary text-[11px] font-semibold transition-colors"
                data-testid="nudge-action"
              >
                {current.cta} <ArrowLeft className="w-3 h-3" />
              </button>

              <button
                onClick={() => handleDismiss(true)}
                className="w-full text-center mt-1.5 text-[10px] text-zinc-600 hover:text-zinc-400 transition-colors"
                data-testid="nudge-mute"
              >
                השתק לסשן הזה
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function buildNudges(h, recs) {
  const out = [];

  if (h.opportunities > 0) {
    out.push({
      kind: "scan_complete",
      eyebrow: "סריקה הושלמה הרגע",
      icon: Activity,
      iconBg: "bg-primary/10",
      iconColor: "text-primary",
      title: `סיימתי לסרוק ${(h.opportunities + h.risks + h.anomalies).toLocaleString()} סיגנלים חדשים`,
      body: "המודולים שמופעלים אצלך: צמיחת מוצרים, פערי מחירים, אנומליות מחיר-עלות.",
      cta: "ראה את הסיכום",
      action: "/insights",
    });
  }

  if (recs && recs.length > 0) {
    const top = recs[0];
    out.push({
      kind: "purchase_rec",
      eyebrow: "המלצת רכש חדשה",
      icon: TrendingUp,
      iconBg: "bg-severity-opportunity/10",
      iconColor: "text-severity-opportunity",
      title: top.product?.length > 38 ? top.product.slice(0, 38) + "…" : top.product,
      body: `על בסיס הקצב הנוכחי — מומלץ ${top.recommended_purchase_qty?.toLocaleString() || "—"} יח׳ לחודש הבא.`,
      cta: "פתח המלצה",
      action: "/insights",
    });
  }

  if (h.anomalies > 0) {
    out.push({
      kind: "anomaly",
      eyebrow: "חשד למחיר/עלות",
      icon: Search,
      iconBg: "bg-severity-critical/10",
      iconColor: "text-severity-critical",
      title: `מצאתי ${h.anomalies} פריט${h.anomalies > 1 ? "ים" : ""} עם יחס מכירה/עלות חריג`,
      body: "מחיר המכירה נמוך מ-70% מהעלות — שווה בדיקה לפני שזה ימשיך.",
      cta: "פתח רשימה",
      action: "/actions?tab=cost_anomalies",
    });
  }

  if (h.risks >= 3) {
    out.push({
      kind: "variance",
      eyebrow: "פערי מחיר זוהו",
      icon: Sparkles,
      iconBg: "bg-severity-watch/10",
      iconColor: "text-severity-watch",
      title: `${h.risks} פריטים נמכרים במחירים שונים`,
      body: "פוטנציאל מהיר לאחידות מחירון או הסכמי הנחה לא מסונכרנים.",
      cta: "ראה פערים",
      action: "/actions?tab=price_variance",
    });
  }

  return out;
}
